<script setup lang="ts">
import { computed, nextTick, shallowRef } from 'vue';
import { LEGACY_GALLERY_TRANSPARENT_PIXEL } from '@/components/legacy-gallery/legacyGalleryTypes';
import { useLegacyCarouselPager } from '@/components/legacy-gallery/useLegacyCarouselPager';
import {
  LEGACY_GALLERY_EMPTY_SELECTION,
  useLegacyGalleryFacade,
} from '@/components/legacy-gallery/useLegacyGalleryFacade';
import { useLegacyGalleryTelemetry } from '@/components/legacy-gallery/useLegacyGalleryTelemetry';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import PrintQueueItemStatus from './PrintQueueItemStatus.vue';
import type { PrintingQueueItem } from './usePrintingFlow';

export interface LegacyPrintQueueCarouselApi {
  scrollToItem: (uuid: string) => void;
}

const props = defineProps<{
  errorSettled: boolean;
  isLandscapeKiosk: boolean;
  isVerticalKiosk: boolean;
  items: readonly PrintingQueueItem[];
  itemsPerSlide: number;
}>();

const imageElements = shallowRef<Array<HTMLImageElement | null>>([]);
const measuredAspectRatios = shallowRef(new Map<string, number>());
const actualItemsPerSlide = computed(() =>
  props.isVerticalKiosk || !props.isLandscapeKiosk ? 1 : props.itemsPerSlide
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const isCentered = computed(() => props.items.length < props.itemsPerSlide);

const telemetry = useLegacyGalleryTelemetry({
  actualItemsPerSlide,
  isGridMode: false,
  items: () => props.items,
  selectedItemUuids: LEGACY_GALLERY_EMPTY_SELECTION,
  selectionMode: null,
});
const pager = useLegacyCarouselPager({
  actualItemsPerSlide,
  isGridMode: false,
  items: () => props.items,
  onSwitchSlide: (
    delta: number,
    firstVisibleItemIndex: number,
    visibleItems: Array<PrintingQueueItem | undefined>
  ) => {
    telemetry.recordSlideSwitch({
      delta,
      firstVisibleItemIndex,
      visibleItems,
    });
  },
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function imageSource(item: PrintingQueueItem): string {
  return item.isPreloaded !== false && item.url
    ? item.url
    : LEGACY_GALLERY_TRANSPARENT_PIXEL;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onItemClick(event: MouseEvent, item: PrintingQueueItem): void {
  telemetry.recordItemClick(event, item);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageError(
  item: PrintingQueueItem,
  index: number,
  event: Event
): void {
  if (item.isPreloaded !== false) {
    telemetry.recordImageLoadError(item, index, event);
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function itemAspectStyle(
  item: PrintingQueueItem
): Record<string, string> | undefined {
  const aspectRatio =
    measuredAspectRatios.value.get(item.uuid) ?? item.aspectRatio;
  return aspectRatio
    ? { '--ImageGallery--item-ratio': String(aspectRatio) }
    : undefined;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageLoad(item: PrintingQueueItem, event: Event): void {
  if (item.isPreloaded === false || !item.url) {
    return;
  }

  const image = event.currentTarget as HTMLImageElement | null;
  if (!(image?.naturalWidth && image.naturalHeight)) {
    return;
  }

  const aspectRatio = image.naturalWidth / image.naturalHeight;
  item.aspectRatio = aspectRatio;
  measuredAspectRatios.value = new Map(measuredAspectRatios.value).set(
    item.uuid,
    aspectRatio
  );
  void nextTick(pager.refreshItems);
}

const {
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setContainerElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setImageElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setItemElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setScrollableElement,
} = useLegacyGalleryFacade({
  actualItemsPerSlide,
  extras: {
    get isVerticalKiosk(): boolean {
      return props.isVerticalKiosk;
    },
    get itemsPerSlide(): number {
      return props.itemsPerSlide;
    },
    get viewportIsPortrait(): boolean {
      return !props.isLandscapeKiosk;
    },
  },
  imageElements,
  items: () => props.items,
  pager,
});

defineExpose<LegacyPrintQueueCarouselApi>({
  scrollToItem: pager.scrollToItem,
});
</script>

<template>
  <div
    :ref="setContainerElement"
    class="ImageGallery"
    :class="{
      'ImageGallery--arrow-prev-hidden': pager.hidePrevArrow.value,
      'ImageGallery--arrow-next-hidden': pager.hideNextArrow.value,
      PrintQueueGallery: true,
      'PrintQueueGallery--legacy': true,
      'PrintQueueGallery--error': errorSettled,
      'PrintQueueGallery--landscape': isLandscapeKiosk,
      'PrintQueueGallery--single-item': items.length === 1,
      'PrintQueueGallery--vertical': isVerticalKiosk,
    }"
  >
    <button
      class="ImageGallery__arrow ImageGallery__arrow--prev"
      @click="pager.switchSlide(-1)"
    />
    <button
      class="ImageGallery__arrow ImageGallery__arrow--next"
      @click="pager.switchSlide(1)"
    />

    <div
      :ref="setScrollableElement"
      class="ImageGallery__scrollable hidden-scrollbar"
      :class="{ 'ImageGallery__scrollable--centered': isCentered }"
    >
      <ul class="ImageGallery__list">
        <li
          v-for="(portrait, index) in items"
          :key="pager.getItemRenderKey(portrait, index)"
          :ref="(element) => setItemElement(element, index)"
          class="ImageGallery__item"
          data-testid="gallery-item"
          data-selected="false"
          :data-loading="
            portrait.isPreloaded === false && !portrait.isError
              ? 'true'
              : 'false'
          "
          :data-error="portrait.isError ? 'true' : 'false'"
          @click="onItemClick($event, portrait)"
        >
          <div
            class="ImageGallery__item-wrapper"
            :style="itemAspectStyle(portrait)"
          >
            <div class="ImageGallery__item-overlay">
              <PrintQueueItemStatus
                :center-error="false"
                :is-landscape-kiosk="isLandscapeKiosk"
                :is-vertical-kiosk="isVerticalKiosk"
                :portrait="portrait"
              />
            </div>

            <img
              :ref="(element) => setImageElement(element, index)"
              class="ImageGallery__item-image sentry-block"
              :src="imageSource(portrait)"
              @error="onImageError(portrait, index, $event)"
              @load="onImageLoad(portrait, $event)"
            />

            <i
              v-show="false"
              class="ImageGallery__item-checkmark kiosk-success-icon"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss">
.PrintQueueGallery--legacy .ImageGallery__item-wrapper {
  border-radius: var(--print-queue-item-radius, 1rem);
  overflow: hidden;
}

.PrintQueueGallery--legacy .ImageGallery__item-image {
  border-radius: inherit;
}
</style>
